import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useRISearch, RISMode, RISMessage, DeepRISearchResponse, RISLinkMetadata, EnhancedSource, StructuredCaseLawResponse } from '../contexts/RISearchContext';
import { useRISViewer } from '../contexts/RISViewerContext';
import { buildApiUrl } from '../config/api';
import { FileText, Calculator, TrendingUp, Target } from 'lucide-react';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import webChatHistoryService from '../services/webChatHistoryService';
import { useAppLayout } from '../components/AppLayout';
import { ErrorHandler } from '../utils/errorHandler';
import { sanitizeHtml } from '../utils/sanitize';
import { convertMarkdownTables, restoreTablePlaceholders } from '../utils/markdownTableParser';

const RISAssistant: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const riSearchContext = useRISearch(); // Global context for background search
  const { isSessionSearching, cancelSearch } = riSearchContext; // Function to check if a specific session is searching
  const { openViewer, openDocumentViewer } = useRISViewer(); // In-app RIS document viewer
  const { currentSessionId, setCurrentSessionId, triggerSidebarRefresh, pendingSessionLoad, clearPendingSessionLoad } = useAppLayout();

  // Always use tooluse mode (RISearch 2.0) - mode selector removed
  const selectedMode: RISMode = 'tooluse';
  const [messages, setMessages] = useState<RISMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [copiedId, setCopiedId] = useState<string>('');
  const [isLoadingSession, setIsLoadingSession] = useState(false);
  const [chatMode, setChatMode] = useState(() => localStorage.getItem('risearch-chat-mode') === 'true');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Ref to track current sessionId for stale closure protection
  const sessionIdRef = useRef<string | null>(null);
  useEffect(() => {
    sessionIdRef.current = sessionId;
  }, [sessionId]);

  // Ref to guard isLoading: only the active search's handleSubmit can clear it
  const activeSubmitIdRef = useRef<string | null>(null);

  // Watch for pending results from background searches (fires on any change, not just mount)
  useEffect(() => {
    if (riSearchContext.pendingResult) {
      const pendingResult = riSearchContext.consumeResult();
      if (pendingResult) {
        const currentSid = sessionIdRef.current;
        if (!currentSid || pendingResult.sessionId === currentSid) {
          console.log('[RISAssistant] Pending result received, adding to messages');
          setMessages(prev => [...prev, pendingResult.userMessage, pendingResult.assistantMessage]);
          if (pendingResult.sessionId && !currentSid) {
            setSessionId(pendingResult.sessionId);
            setCurrentSessionId(pendingResult.sessionId);
          }
          triggerSidebarRefresh();
        } else {
          console.log(`[RISAssistant] Pending result is for different session (${pendingResult.sessionId}), skipping`);
        }
      }
    }
  }, [riSearchContext.pendingResult]);

  // Handle pending session load from sidebar
  useEffect(() => {
    if (pendingSessionLoad) {
      handleSessionSelect(pendingSessionLoad.sessionId);
      clearPendingSessionLoad();
    }
  }, [pendingSessionLoad]);

  // Initialize session ID on mount if not already set
  useEffect(() => {
    const initSession = async () => {
      if (!sessionId && !currentSessionId) {
        const newSessionId = await webChatHistoryService.generateSessionId('ris', user?.id);
        setSessionId(newSessionId);
        setCurrentSessionId(newSessionId);
        console.log(`[RIS] Initialized new session on mount: ${newSessionId}`);
      } else if (currentSessionId && currentSessionId !== sessionId) {
        // Check if this is a new session (not loading an existing one)
        // New sessions start with pattern: userId_ris_timestamp_random
        const isNewSession = currentSessionId.includes('_ris_') && !pendingSessionLoad;

        if (isNewSession) {
          // Clear messages and loading state for new session
          console.log(`[RIS] New session detected, clearing state: ${currentSessionId}`);
          setMessages([]);
          setIsLoading(false);
          activeSubmitIdRef.current = null; // Invalidate old handleSubmit's finally block
          setError(null);
        }

        // Sync with AppLayout's currentSessionId
        setSessionId(currentSessionId);
      }
    };
    initSession();
  }, [user?.id, currentSessionId]); // Re-run if user or currentSessionId changes

  // Sync loading state with context - use session-specific check for parallel search support
  // This ensures the loading indicator shows when THIS session is searching,
  // even if user switches away and back while search is in progress
  const effectiveIsLoading = isLoading || (sessionId ? isSessionSearching(sessionId) : false);

  // Icon for RISearch 2.0
  const getModeIcon = (className: string = "w-8 h-8") => {
    return <MagnifyingGlassIcon className={className} />;
  };

  // HELPER: Convert old sources array to verifiedSources format for backward compatibility
  const convertSourcesToVerifiedSources = (sources: string[]): EnhancedSource[] => {
    if (!sources || sources.length === 0) return [];

    const verifiedSources: EnhancedSource[] = [];
    const risUrlPattern = /https?:\/\/www\.ris\.bka\.gv\.at\/eli\/jgs\/\d+\/\d+\/P(\d+)\/NOR\d+/;
    const paragraphPattern = /\u00A7\s*\d+[a-zA-Z]*\s+[A-Z]+(?:\s+[A-Z]+)*/;

    // Group URLs and paragraphs
    const urls: { url: string; paragraph?: string }[] = [];
    const paragraphs: string[] = [];

    sources.forEach(source => {
      const cleanSource = source.replace(/^\[\d+\]\s*/, ''); // Remove [1] prefix if present

      if (risUrlPattern.test(cleanSource)) {
        urls.push({ url: cleanSource });
      } else if (paragraphPattern.test(cleanSource)) {
        paragraphs.push(cleanSource);
      }
    });

    // Try to match paragraphs with URLs based on paragraph number
    paragraphs.forEach(paragraph => {
      const paragraphMatch = paragraph.match(/\u00A7\s*(\d+)[a-zA-Z]*\s+([A-Z]+(?:\s+[A-Z]+)*)/);
      if (paragraphMatch) {
        const paragraphNum = paragraphMatch[1];

        // Find matching URL
        const matchingUrl = urls.find(urlObj =>
          urlObj.url.includes(`/P${paragraphNum}/`) && !urlObj.paragraph
        );

        if (matchingUrl) {
          matchingUrl.paragraph = paragraph;
          verifiedSources.push({
            type: 'paragraph',
            reference: paragraph,
            title: 'Paragraph', // Generic title for old sources
            risLink: matchingUrl.url,
            found: true
          });
        } else {
          // Paragraph without URL
          verifiedSources.push({
            type: 'paragraph',
            reference: paragraph,
            title: 'Paragraph',
            found: false
          });
        }
      }
    });

    // Add remaining URLs without paragraphs
    urls.forEach(urlObj => {
      if (!urlObj.paragraph) {
        const urlMatch = urlObj.url.match(/\/P(\d+)\//);
        const paragraphNum = urlMatch ? urlMatch[1] : 'Unknown';

        verifiedSources.push({
          type: 'paragraph',
          reference: `\u00A7 ${paragraphNum}`,
          title: 'Paragraph',
          risLink: urlObj.url,
          found: true
        });
      }
    });

    console.log(`[RISAssistant] Converted ${sources.length} old sources to ${verifiedSources.length} verifiedSources`);
    return verifiedSources;
  };

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Session Event Handlers
  const handleSessionSelect = async (selectedSessionId: string) => {
    // Skip if already loading or same session
    if (isLoadingSession) {
      console.log(`[RIS] Skipping session select - already loading`);
      return;
    }
    if (selectedSessionId === sessionId) {
      console.log(`[RIS] Skipping session select - same session`);
      return;
    }

    console.log(`[RIS] Loading session: ${selectedSessionId}`);

    // Check if target session is currently searching
    const targetSessionIsSearching = isSessionSearching(selectedSessionId);
    if (targetSessionIsSearching) {
      console.log(`[RIS] Target session ${selectedSessionId} is currently searching, switching with loading state`);
    }

    try {
      setIsLoadingSession(true);
      setError(null);
      setMessages([]); // Clear messages immediately to show loading state

      // If the target session is searching, just switch to it without loading messages
      // The search result will update the messages when it completes
      if (targetSessionIsSearching) {
        setCurrentSessionId(selectedSessionId);
        setSessionId(selectedSessionId);
        console.log(`[RIS] Switched to searching session: ${selectedSessionId}`);
        setIsLoadingSession(false);
        return;
      }

      // Load session from API
      const { session, messages: sessionMessages } = await webChatHistoryService.getChatSession(selectedSessionId);

      if (!session || !sessionMessages) {
        throw new Error('Session not found');
      }

      setCurrentSessionId(selectedSessionId);
      setSessionId(selectedSessionId);

      // Convert messages to RIS format
      const risMessages: RISMessage[] = sessionMessages.map(msg => ({
        role: msg.role === 'user' ? 'user' : 'assistant',
        content: msg.content,
        timestamp: msg.timestamp ? new Date(msg.timestamp) : new Date(),
        mode: (msg.metadata?.serviceType as RISMode) || 'general',
        sources: msg.metadata?.sources,
        verifiedSources: msg.metadata?.verifiedSources ?
          msg.metadata.verifiedSources.map((vs: any) => ({
            type: vs.type || 'paragraph',
            reference: vs.reference || vs.title,
            title: vs.title || vs.reference,
            risLink: vs.risLink || vs.url,
            found: vs.found !== undefined ? vs.found : (vs.verified !== undefined ? vs.verified : true),
            confidence: vs.confidence
          })) :
          // FALLBACK: Convert sources to verifiedSources format if they contain RIS links
          (msg.metadata?.sources) ?
            convertSourcesToVerifiedSources(msg.metadata.sources) : undefined,
        sourceVerificationStatus: msg.metadata?.sourceVerificationStatus ||
          // FALLBACK: Set status to 'completed' if we have converted sources
          ((msg.metadata?.sources) && !(msg.metadata?.verifiedSources) ? 'completed' : undefined),
        messageId: msg.metadata?.messageId,
        risLinkMetadata: msg.metadata?.risLinkMetadata
      }));

      setMessages(risMessages);

      console.log(`[RIS] Loaded session: ${selectedSessionId} with ${risMessages.length} messages`);

      // Restore session history in backend for continued conversation context
      if (risMessages.length > 0) {
        try {
          // Cookies are sent automatically with withCredentials: true
          console.log(`[Frontend] Restoring backend session context for: ${selectedSessionId}`);
          const response = await axios.post(buildApiUrl('/api/ris/restore-session-history'), {
            sessionId: selectedSessionId,
            history: risMessages
          }, {
            withCredentials: true
          });

          if (response.data.success) {
            console.log(`[Frontend] Backend session context restored with ${risMessages.length} messages`);
          } else {
            console.warn(`[Frontend] Backend session restore failed: ${response.data.error}`);
          }
        } catch (restoreError) {
          console.warn('[Frontend] Backend session restore failed, continuing without context:', restoreError);
        }
      }

    } catch (error) {
      console.error('[RIS] Error loading session:', error);
      setError('Fehler beim Laden der Session');
    } finally {
      setIsLoadingSession(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Check both local and context loading states
    if (!inputValue.trim() || effectiveIsLoading) return;

    if (inputValue.trim().length < 10) {
      setError('Bitte geben Sie eine aussagekräftige Frage mit mindestens 10 Zeichen ein.');
      return;
    }

    const query = inputValue.trim();

    // Add user message to local state immediately for UI feedback
    const userMessage: RISMessage = {
      role: 'user',
      content: query,
      timestamp: new Date(),
      mode: 'tooluse',
      messageId: `${user?.id}_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`
    };

    // Track this submit so old handleSubmit's finally can't kill our loading state
    const submitId = `${Date.now()}_${Math.random().toString(36).substring(2, 6)}`;
    activeSubmitIdRef.current = submitId;

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);
    setError(null);

    try {
      let localSessionId = sessionId;

      // Ensure we have a session ID
      if (!localSessionId) {
        console.warn(`[RIS] No session ID found, generating fallback...`);
        localSessionId = await webChatHistoryService.generateSessionId('ris', user?.id);
        setSessionId(localSessionId);
        setCurrentSessionId(localSessionId);
        console.warn(`[RIS] Generated fallback session ID: ${localSessionId}`);
      } else {
        console.log(`[RIS] Using existing session ID: ${localSessionId}`);
      }

      // Capture session ID before await to detect stale closure
      const capturedSessionId = localSessionId;

      // Delegate to global context - this will continue even if user navigates away
      // startSearch now returns the result directly to avoid React state timing issues
      const result = await riSearchContext.startSearch(
        query,
        selectedMode,
        localSessionId,
        messages, // Pass existing messages for chat history context
        sessionId
      );

      // If we got a result, add assistant message to UI only if session hasn't changed
      if (result) {
        if (capturedSessionId === sessionIdRef.current) {
          setMessages(prev => [...prev, result.assistantMessage]);
          console.log(`[RIS] Search completed while on page, same session`);
          // Consume to prevent the pendingResult watcher from double-adding
          riSearchContext.consumeResult();
        } else {
          console.log(`[RIS] Session changed during search (was ${capturedSessionId}, now ${sessionIdRef.current}), skipping UI update`);
        }
        triggerSidebarRefresh();
      }

    } catch (error: any) {
      console.error('RIS search error:', error);
      // Only set error if this submit is still the active one
      if (activeSubmitIdRef.current === submitId) {
        setError(riSearchContext.searchError || ErrorHandler.handleApiError(error));
      }
    } finally {
      // Only clear isLoading if this submit is still the active one
      // Prevents old search's finally from killing a new search's loading state
      if (activeSubmitIdRef.current === submitId) {
        setIsLoading(false);
      }
    }
  };

  const copyToClipboard = async (text: string, type?: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(`${text}-${type || 'default'}`);
      setTimeout(() => setCopiedId(''), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const copyMessageWithSources = async (message: RISMessage) => {
    try {
      let textToCopy = message.content;

      await navigator.clipboard.writeText(textToCopy);
      setCopiedId(`${message.content}-with-sources`);
      setTimeout(() => setCopiedId(''), 2000);
    } catch (err) {
      console.error('Failed to copy message with sources: ', err);
    }
  };

  const toggleChatMode = () => {
    const newMode = !chatMode;
    setChatMode(newMode);
    localStorage.setItem('risearch-chat-mode', String(newMode));
  };

  const autoResizeTextarea = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = Math.min(textarea.scrollHeight, 180) + 'px';
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (chatMode) {
      // Chat mode: Enter = send, Shift+Enter = newline
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        if (inputValue.trim() && !effectiveIsLoading) {
          handleSubmit(e as unknown as React.FormEvent);
        }
      }
    } else {
      // Typing mode (default): Enter = newline, Shift+Enter = send
      if (e.key === 'Enter' && e.shiftKey) {
        e.preventDefault();
        if (inputValue.trim() && !effectiveIsLoading) {
          handleSubmit(e as unknown as React.FormEvent);
        }
      }
    }
  };

  const clearSession = () => {
    setMessages([]);
    setSessionId(null);
    setError(null);
  };

  // Helper to create RIS link HTML with data attributes for in-app viewer
  const escHtml = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  const createRISLinkHtml = (url: string, text: string, className: string = 'text-blue-600 hover:text-blue-800 underline font-medium') => {
    const safeUrl = escHtml(url);
    const safeText = escHtml(text);
    // Check if this is a RIS URL that should open in the viewer
    const isRISUrl = url.includes('ris.bka.gv.at');
    if (isRISUrl) {
      return `<a href="${safeUrl}" data-ris-url="${safeUrl}" class="${className} cursor-pointer">${safeText}</a>`;
    }
    return `<a href="${safeUrl}" target="_blank" rel="noopener noreferrer" class="${className}">${safeText}</a>`;
  };

  const formatMessage = (content: string, risLinkMetadata?: RISLinkMetadata) => {
    // Step 1: Extract markdown tables and replace with placeholders
    const { content: contentWithPlaceholders, tables } = convertMarkdownTables(content);

    // Step 2: Enhanced formatting with URL handling, word breaking, and Markdown headers
    let formatted = contentWithPlaceholders
      // Convert Markdown headers to HTML (#### -> h4, ### -> h3, ## -> h2, # -> h1)
      .replace(/^#### (.*$)/gm, '<h4 class="text-base font-semibold text-gray-900 dark:text-gray-100 mt-3 mb-1">$1</h4>')
      .replace(/^### (.*$)/gm, '<h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100 mt-4 mb-2">$1</h3>')
      .replace(/^## (.*$)/gm, '<h2 class="text-xl font-bold text-gray-900 dark:text-gray-100 mt-6 mb-3">$1</h2>')
      .replace(/^# (.*$)/gm, '<h1 class="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-8 mb-4">$1</h1>')
      // Convert lines starting with * to bullet points
      .replace(/^\* (.*)$/gm, '<li class="ml-4 list-disc list-inside">$1</li>')
      // Convert numbered lists (1. item, 2. item)
      .replace(/^(\d+)\.\s+(.*)$/gm, '<li class="ml-4 list-decimal list-inside" value="$1">$2</li>')
      // Convert three dashes to divider
      .replace(/^---\s*\n*/gm, '<hr class="mt-0 mb-6 border-t border-gray-300">')
      // Bold and italic formatting
      .replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold">$1</strong>')
      .replace(/\*(.*?)\*/g, '<em class="italic">$1</em>')
      // Convert paragraph references with URLs to clean clickable links
      .replace(/(\u00A7\s*\d+[a-zA-Z]*(?:\s+(?:Abs\.?\s*\d+|[A-Za-z0-9]+))+)\s*\((https?:\/\/[^\s)]+)\)/g,
        (_, text, url) => createRISLinkHtml(url, text))
      // Also handle square brackets for paragraph references
      .replace(/(\u00A7\s*\d+[a-zA-Z]*(?:\s+(?:Abs\.?\s*\d+|[A-Za-z0-9]+))+)\s*\[(https?:\/\/[^\s\]]+)\]/g,
        (_, text, url) => createRISLinkHtml(url, text))
      // Convert Rechtssatz references with URLs to clickable links
      .replace(/\b(RS\d+)\s*\((https?:\/\/[^\s)]+)\)/g,
        (_, text, url) => createRISLinkHtml(url, text))
      .replace(/\b(RS\d+)\s*\[(https?:\/\/[^\s\]]+)\]/g,
        (_, text, url) => createRISLinkHtml(url, text))
      // Convert court decision references with URLs
      .replace(/\b((?:OGH|VfGH|VwGH|BVwG|LVwG|OLG)[^\s()\[\]<>]*(?:\s+[^\s()\[\]<>]+)*)\s*\((https?:\/\/[^\s)]+)\)/g,
        (_, text, url) => createRISLinkHtml(url, text))
      .replace(/\b((?:OGH|VfGH|VwGH|BVwG|LVwG|OLG)[^\s()\[\]<>]*(?:\s+[^\s()\[\]<>]+)*)\s*\[(https?:\/\/[^\s\]]+)\]/g,
        (_, text, url) => createRISLinkHtml(url, text))
      // ELI-Links
      .replace(/\((https?:\/\/(?:www\.)?ris\.bka\.gv\.at\/eli\/[^\/]+\/[^\/]+\/\d+\/P(\d+)[^)]*)\)/g,
        (_, url, para) => ' ' + createRISLinkHtml(url, `[\u00A7 ${para}]`, 'text-blue-600 hover:text-blue-800 underline'))
      .replace(/\((https?:\/\/(?:www\.)?ris\.bka\.gv\.at\/eli\/[^)]+)\)/g,
        (_, url) => ' ' + createRISLinkHtml(url, '[Gesetz]', 'text-blue-600 hover:text-blue-800 underline'))
      .replace(/\((https?:\/\/(?:www\.)?ris\.bka\.gv\.at[^)]+)\)/g,
        (_, url) => ' ' + createRISLinkHtml(url, '[RIS]', 'text-blue-600 hover:text-blue-800 underline'))
      // BGBl references: "BGBl I 2025/111", "BGBl. II Nr. 50/2024", "BGBl I Nr. 111/2025"
      .replace(/BGBl\.?\s+(I{1,3})\s+(?:Nr\.?\s+)?(\d{1,4})\/(\d{4})/g,
        (match, teil, nummer, jahr) => {
          const teilMap: Record<string, string> = { 'I': '1', 'II': '2', 'III': '3' };
          const bgblTeil = teilMap[teil] || '1';
          const url = `https://www.ris.bka.gv.at/Ergebnis.wxe?Abfrage=BgblAuth&Bgblnummer=${encodeURIComponent(nummer + '/' + jahr)}&BgblTeil=${bgblTeil}`;
          return createRISLinkHtml(url, match);
        })
      // Convert enriched document references (dokumentId:uuid|title|sourceUrl) to titled clickable links
      .replace(/\(dokumentId:([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\|([^|]*)\|([^)]*)\)/gi,
        (_, docId, title, sourceUrl) => {
          const linkText = (title.trim() || 'Dokument anzeigen').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
          const viewerLink = `<a href="#" data-document-id="${docId}" class="text-blue-600 hover:text-blue-800 underline cursor-pointer font-medium">${linkText}</a>`;
          const trimmedUrl = sourceUrl?.trim();
          if (trimmedUrl && /^https?:\/\//i.test(trimmedUrl)) {
            return `${viewerLink} <a href="${escHtml(trimmedUrl)}" target="_blank" rel="noopener noreferrer" class="text-gray-500 hover:text-gray-700 text-xs ml-1" title="WKO Quelle">[Quelle]</a>`;
          }
          return viewerLink;
        })
      // Fallback: Convert plain document references (dokumentId: uuid) without enrichment
      .replace(/\(dokumentId:\s*([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\)/gi,
        (_, docId) => `<a href="#" data-document-id="${docId}" class="text-blue-600 hover:text-blue-800 underline cursor-pointer font-medium">[Dokument anzeigen]</a>`)
      // Fallback 2: bare dokumentId without parentheses (e.g. ", dokumentId: UUID")
      .replace(/(?:,\s*)?dokumentId:\s*([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/gi,
        (_, docId) => ` <a href="#" data-document-id="${docId}" class="text-blue-600 hover:text-blue-800 underline cursor-pointer font-medium">[Dokument anzeigen]</a>`)
      // Other URLs
      .replace(/\[(https?:\/\/(?!(?:www\.)?ris\.bka\.gv\.at)[^\s\]]+)\]/g, ' <a href="$1" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:text-blue-800 underline">[Link]</a>')
      .replace(/\[(https?:\/\/(?:www\.)?ris\.bka\.gv\.at[^\s\]]+)\]/g,
        (_, url) => ' ' + createRISLinkHtml(url, '[RIS]', 'text-blue-600 hover:text-blue-800 underline'))
      // Convert Markdown links [text](url)
      .replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g,
        (_, text, url) => createRISLinkHtml(url, text))
      // Convert remaining standalone URLs to clickable links
      .replace(/(^|[^">])(https?:\/\/[^\s<)]+)/g, (match, prefix, url) => {
        if (url.includes('ris.bka.gv.at')) {
          return prefix + createRISLinkHtml(url, url, 'text-blue-600 hover:text-blue-800 underline break-all');
        }
        return `${prefix}<a href="${url}" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:text-blue-800 underline break-all">${url}</a>`;
      })
      // Reduce double line breaks after headers
      .replace(/(<\/h[1-6]>)\n\n/g, '$1\n')
      // Convert line breaks to HTML
      .replace(/\n/g, '<br>');

    // Step 3: Restore table HTML from placeholders (after \n -> <br> conversion)
    formatted = restoreTablePlaceholders(formatted, tables);

    // Step 4: Apply RIS Link hyperlinks if metadata is available
    let finalFormatted = formatted;

    if (risLinkMetadata) {
      // Convert Geschaeftszahlen to hyperlinks
      risLinkMetadata.geschaeftszahlen.forEach(({geschaeftszahl, risUrl}) => {
        const escapedGeschaeftszahl = geschaeftszahl.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const regex = new RegExp(`(?<!\\w)${escapedGeschaeftszahl}(?!\\w)`, 'g');
        finalFormatted = finalFormatted.replace(regex, (match, offset, fullStr) => {
          const before = fullStr.substring(Math.max(0, offset - 500), offset);
          const lastAOpen = before.lastIndexOf('<a ');
          const lastAClose = before.lastIndexOf('</a>');
          if (lastAOpen > lastAClose) return match;
          return createRISLinkHtml(risUrl, match);
        });
      });

      // Convert Paragraphen to hyperlinks
      risLinkMetadata.paragraphen.forEach(({paragraph, risLink}) => {
        const escapedParagraph = paragraph.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const regex = new RegExp(`(?<!\\w)${escapedParagraph}(?!\\w)`, 'g');
        finalFormatted = finalFormatted.replace(regex, (match, offset, fullStr) => {
          const before = fullStr.substring(Math.max(0, offset - 500), offset);
          const lastAOpen = before.lastIndexOf('<a ');
          const lastAClose = before.lastIndexOf('</a>');
          if (lastAOpen > lastAClose) return match;
          return createRISLinkHtml(risLink, match);
        });
      });
    }

    return { __html: sanitizeHtml(finalFormatted) };
  };

  // Handle RIS link clicks - open in-app viewer instead of new tab
  const handleRISLinkClick = useCallback((e: React.MouseEvent<HTMLDivElement>, messageContent?: string) => {
    const target = e.target as HTMLElement;

    // Check for document viewer links first
    const docLink = target.closest('a[data-document-id]') as HTMLAnchorElement | null;
    if (docLink) {
      e.preventDefault();
      e.stopPropagation();
      const documentId = docLink.getAttribute('data-document-id');
      if (documentId) {
        const lastUserMessage = messages.filter(m => m.role === 'user').pop();
        const query = lastUserMessage?.content || inputValue || '';
        openDocumentViewer(documentId, query, messageContent);
      }
      return;
    }

    // Existing RIS URL handling
    const link = target.closest('a[data-ris-url]') as HTMLAnchorElement | null;

    if (link) {
      e.preventDefault();
      e.stopPropagation();

      const risUrl = link.getAttribute('data-ris-url');
      if (risUrl) {
        // Get the last user query for context
        const lastUserMessage = messages.filter(m => m.role === 'user').pop();
        const query = lastUserMessage?.content || inputValue || '';

        // Use the message content as context for better highlighting
        openViewer(risUrl, query, messageContent);
      }
    }
  }, [messages, inputValue, openViewer, openDocumentViewer]);

  // ===== Collapsible Sections Helpers (E and F) =====
  const [expandedSections, setExpandedSections] = useState<Record<string, { E: boolean; F: boolean }>>({});

  const toggleSection = (messageKey: string, section: 'E' | 'F') => {
    setExpandedSections(prev => {
      const current = prev[messageKey] || { E: false, F: false };
      return { ...prev, [messageKey]: { ...current, [section]: !current[section] } };
    });
  };

  const escapeRegExp = (str: string) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  // Find top-level section (### A. ... style) range by letter and title
  const findTopLevelSectionRange = (
    content: string,
    letter: 'E' | 'F',
    title: string
  ): { start: number; end: number } | null => {
    const headerRegex = new RegExp(`^###\\s+${letter}\\.\\s+${escapeRegExp(title)}\\s*$`, 'm');
    const match = content.match(headerRegex);
    if (!match) return null;
    const start = match.index ?? 0;
    const afterHeaderOffset = start + match[0].length;
    // Next top-level header like "### X. ..." (single capital letter + dot)
    const nextHeaderRegex = /^###\s+[A-Z]\.\s+/m;
    const rest = content.slice(afterHeaderOffset);
    const nextIdxInRest = rest.search(nextHeaderRegex);
    const end = nextIdxInRest >= 0 ? afterHeaderOffset + nextIdxInRest : content.length;
    return { start, end };
  };

  // Split a section by case separators (---) and return preview (first 3 entries) and the remaining
  const splitSectionPreview = (
    sectionContent: string,
    maxEntries: number = 3
  ): { preview: string; rest: string } => {
    const sepRegex = /\n-{3,}\n+/g;
    let count = 0;
    let match: RegExpExecArray | null;
    let cutoff = -1;
    while ((match = sepRegex.exec(sectionContent)) !== null) {
      count++;
      if (count === maxEntries) {
        cutoff = match.index + match[0].length;
        break;
      }
    }
    if (cutoff === -1) {
      // Less than maxEntries separators -> no preview split
      return { preview: sectionContent, rest: '' };
    }
    return {
      preview: sectionContent.slice(0, cutoff),
      rest: sectionContent.slice(cutoff)
    };
  };

  const renderAssistantMessageContent = (message: RISMessage, index: number) => {
    const messageKey = message.messageId || `msg-${index}`;
    const content = message.content || '';

    // Locate sections E and F
    const eRange = findTopLevelSectionRange(content, 'E', 'Relevante Gerichtsentscheidungen');
    const fRange = findTopLevelSectionRange(content, 'F', 'Relevante Rechtsprechung');

    if (!eRange && !fRange) {
      // Fallback: render whole content as-is
      return (
        <div
          className={`prose max-w-none break-words text-gray-900 dark:text-gray-100`}
          style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}
          onClick={(e) => handleRISLinkClick(e, content)}
          dangerouslySetInnerHTML={formatMessage(content, message.risLinkMetadata)}
        />
      );
    }

    // Determine ordered sections by start index
    const sections: Array<{
      id: 'E' | 'F';
      range: { start: number; end: number };
      title: string;
    }> = [];
    if (eRange) sections.push({ id: 'E', range: eRange, title: 'Relevante Gerichtsentscheidungen' });
    if (fRange) sections.push({ id: 'F', range: fRange, title: 'Relevante Rechtsprechung' });
    sections.sort((a, b) => a.range.start - b.range.start);

    const parts: React.ReactNode[] = [];
    let cursor = 0;
    const expanded = expandedSections[messageKey] || { E: false, F: false };

    sections.forEach((sec, idx) => {
      const { start, end } = sec.range;
      // Add content before this section
      if (cursor < start) {
        const before = content.slice(cursor, start);
        if (before.trim()) {
          parts.push(
            <div
              key={`before-${idx}`}
              dangerouslySetInnerHTML={formatMessage(before, message.risLinkMetadata)}
            />
          );
        }
      }

      const sectionText = content.slice(start, end);
      const { preview, rest } = splitSectionPreview(sectionText, 3);

      // Always render preview
      parts.push(
        <div
          key={`sec-${sec.id}-preview-${idx}`}
          dangerouslySetInnerHTML={formatMessage(preview, message.risLinkMetadata)}
        />
      );

      // Render toggle and (optionally) rest
      if (rest && rest.trim()) {
        const isExpanded = expanded[sec.id];
        parts.push(
          <div key={`sec-${sec.id}-toggle-${idx}`} className="mt-0 flex justify-center">
            {!isExpanded ? (
              <button
                onClick={() => toggleSection(messageKey, sec.id)}
                className="text-sm text-blue-600 hover:text-blue-700 underline"
              >
                Mehr anzeigen
              </button>
            ) : (
              <button
                onClick={() => toggleSection(messageKey, sec.id)}
                className="text-sm text-blue-600 hover:text-blue-700 underline"
              >
                Weniger anzeigen
              </button>
            )}
          </div>
        );

        if (isExpanded) {
          parts.push(
            <div
              key={`sec-${sec.id}-rest-${idx}`}
              className="mt-2"
              dangerouslySetInnerHTML={formatMessage(rest, message.risLinkMetadata)}
            />
          );
        }
      }

      cursor = end;
    });

    // Add any trailing content after the last section
    if (cursor < content.length) {
      const tail = content.slice(cursor);
      if (tail.trim()) {
        parts.push(
          <div
            key={`after-tail`}
            dangerouslySetInnerHTML={formatMessage(tail, message.risLinkMetadata)}
          />
        );
      }
    }

    return (
      <div
        className={`prose max-w-none break-words text-gray-900 dark:text-gray-100`}
        style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}
        onClick={(e) => handleRISLinkClick(e, content)}
      >
        {parts}
      </div>
    );
  };

  const renderDeepRISearchData = (deepData: DeepRISearchResponse) => {
    return (
      <div className="mt-4 space-y-4">
        {/* Processing Information */}
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <MagnifyingGlassIcon className="w-5 h-5 text-purple-600" />
            <h4 className="font-semibold text-purple-900">DeepRISearch Analyse</h4>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
              <p className="text-gray-600 mb-1">Verarbeitungszeit</p>
              <p className="font-medium text-purple-600">{Math.round(deepData.metadata.processingTime / 1000)}s</p>
            </div>
            {deepData.metadata.jurisprudenceStats && (
              <>
                <div>
                  <p className="text-gray-600 mb-1">RIS Ergebnisse</p>
                  <p className="font-medium text-purple-600">{deepData.metadata.jurisprudenceStats.searchResults}</p>
                </div>
                <div>
                  <p className="text-gray-600 mb-1">Urteile durchgelesen</p>
                  <p className="font-medium text-purple-600">
                    {deepData.metadata.jurisprudenceStats.extractedCases + (deepData.metadata.jurisprudenceStats.extractedUrteile || 0)}
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderStructuredData = (structuredData: StructuredCaseLawResponse) => {
    const getStrengthColor = (strength: string) => {
      switch (strength) {
        case 'VERY_STRONG': return 'text-green-600 bg-green-100';
        case 'STRONG': return 'text-green-500 bg-green-50';
        case 'MODERATE': return 'text-yellow-600 bg-yellow-100';
        case 'WEAK': return 'text-red-600 bg-red-100';
        default: return 'text-gray-600 bg-gray-100';
      }
    };

    const getConsistencyColor = (consistency: string) => {
      switch (consistency) {
        case 'HIGH': return 'text-green-600';
        case 'MEDIUM': return 'text-yellow-600';
        case 'LOW': return 'text-red-600';
        default: return 'text-gray-600';
      }
    };

    return (
      <div className="mt-4 space-y-4">
        {/* Executive Summary */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <Target className="w-5 h-5 text-blue-600" />
            <h4 className="font-semibold text-blue-900">Strategische Bewertung</h4>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-600 mb-1">Erfolgswahrscheinlichkeit</p>
              <p className="text-xl font-bold text-blue-600">{structuredData.executiveSummary.successProbability}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-1">Gesamtbewertung</p>
              <p className="text-sm">{structuredData.executiveSummary.strategicAssessment}</p>
            </div>
          </div>

          {structuredData.executiveSummary.keyStrengths.length > 0 && (
            <div className="mt-3">
              <p className="text-sm font-medium text-green-700 mb-1">Stärken:</p>
              <ul className="text-sm text-green-600 list-disc list-inside">
                {structuredData.executiveSummary.keyStrengths.map((strength, index) => (
                  <li key={index}>{strength}</li>
                ))}
              </ul>
            </div>
          )}

          {structuredData.executiveSummary.potentialWeaknesses.length > 0 && (
            <div className="mt-3">
              <p className="text-sm font-medium text-red-700 mb-1">Schwächen:</p>
              <ul className="text-sm text-red-600 list-disc list-inside">
                {structuredData.executiveSummary.potentialWeaknesses.map((weakness, index) => (
                  <li key={index}>{weakness}</li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Quality Metrics */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <Calculator className="w-5 h-5 text-gray-600" />
            <h4 className="font-semibold text-gray-900">Qualitätsmetriken</h4>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-gray-600">Entscheidungen</p>
              <p className="text-lg font-bold text-gray-900">{structuredData.qualityMetrics.totalRelevantDecisions}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Confidence</p>
              <p className="text-lg font-bold text-gray-900">{structuredData.qualityMetrics.averageConfidenceScore}%</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Konsistenz</p>
              <p className={`text-sm font-medium ${getConsistencyColor(structuredData.qualityMetrics.jurisprudenceConsistency)}`}>
                {structuredData.qualityMetrics.jurisprudenceConsistency}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Stärke</p>
              <span className={`text-xs px-2 py-1 rounded-full ${getStrengthColor(structuredData.qualityMetrics.argumentationStrength)}`}>
                {structuredData.qualityMetrics.argumentationStrength}
              </span>
            </div>
          </div>
        </div>

        {/* Primary Jurisprudence */}
        {structuredData.primaryJurisprudence.length > 0 && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp className="w-5 h-5 text-green-600" />
              <h4 className="font-semibold text-green-900">Hauptstützende Rechtsprechung</h4>
            </div>

            <div className="space-y-3">
              {structuredData.primaryJurisprudence.slice(0, 3).map((decision, index) => (
                <div key={index} className="bg-white border border-green-200 rounded p-3">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <span className="text-sm font-medium text-green-700">
                        {decision.rsNumber || decision.court}
                      </span>
                      {decision.date && (
                        <span className="text-sm text-gray-500 ml-2">{decision.date}</span>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded">
                        {decision.significance}
                      </span>
                      <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded">
                        {decision.confidenceScore}%
                      </span>
                    </div>
                  </div>

                  <p className="text-sm text-gray-700 mb-2">{decision.coreRuling}</p>

                  {decision.directApplicability && (
                    <p className="text-xs text-gray-600 mb-2">
                      <strong>Anwendbarkeit:</strong> {decision.directApplicability}
                    </p>
                  )}

                  {decision.risLink && (
                    <a
                      href={decision.risLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-blue-600 hover:text-blue-800 underline"
                    >
                      RIS-Link
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Ready-to-Use Text Blocks */}
        {structuredData.readyToUseTextBlocks.length > 0 && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3">
              <FileText className="w-5 h-5 text-yellow-600" />
              <h4 className="font-semibold text-yellow-900">Berufungsfertige Textbausteine</h4>
            </div>

            <div className="space-y-3">
              {structuredData.readyToUseTextBlocks.map((block, index) => (
                <div key={index} className="bg-white border border-yellow-200 rounded p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-yellow-700">{block.type}</span>
                    <button
                      onClick={() => copyToClipboard(block.text, `textblock-${index}`)}
                      className="text-xs px-2 py-1 bg-yellow-100 text-yellow-700 rounded hover:bg-yellow-200 transition-colors"
                    >
                      {copiedId === `${block.text}-textblock-${index}` ? 'Kopiert' : 'Kopieren'}
                    </button>
                  </div>

                  <p className="text-sm text-gray-700 leading-relaxed">{block.text}</p>

                  {block.supportingRsNumbers.length > 0 && (
                    <div className="mt-2">
                      <p className="text-xs text-gray-600">
                        <strong>Stützende RS-Nummern:</strong> {block.supportingRsNumbers.join(', ')}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Chat interface
  return (
    <div className="h-full flex flex-col bg-white dark:bg-gray-800">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-[rgba(30,77,107,0.1)] dark:border-gray-700 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="flex items-center space-x-2">
              <span className="text-2xl text-primary dark:text-white">{getModeIcon()}</span>
              <div>
                <h1 className="text-xl font-bold text-primary dark:text-white tracking-tight">
                  RISearch 2.0
                </h1>
                <p className="text-xs text-gray-500 dark:text-gray-400">KI-gestützte Rechtsrecherche</p>
              </div>
            </div>
          </div>
        </div>
      </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-white dark:bg-gray-900">
          {isLoadingSession ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent mx-auto mb-4"></div>
              <div className="text-muted dark:text-gray-500 text-lg">Session wird geladen...</div>
            </div>
          ) : messages.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-muted dark:text-gray-500 text-lg mb-2">Stellen Sie Ihre erste Frage</div>
              <div className="text-muted/70 dark:text-gray-400 text-sm">
                RISearch 2.0 durchsucht österreichische Rechtsquellen für Sie
              </div>
            </div>
          ) : null}

          {messages.map((message, index) => (
            <div
              key={index}
              className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-3xl rounded-2xl p-4 shadow-soft ${
                  message.role === 'user'
                    ? 'bg-primary text-white'
                    : 'bg-white dark:bg-gray-800 border border-[rgba(30,77,107,0.1)] dark:border-gray-700 text-primary dark:text-gray-100'
                }`}
              >
                {message.role === 'assistant' ? (
                  renderAssistantMessageContent(message, index)
                ) : (
                  <div
                    className={`prose max-w-none break-words ${message.role === 'user' ? 'text-white' : 'text-gray-900 dark:text-gray-100'}`}
                    style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}
                    dangerouslySetInnerHTML={formatMessage(message.content, message.risLinkMetadata)}
                  />
                )}

                {message.role === 'assistant' && (
                  <div className="mt-4 flex items-center justify-end border-t border-[rgba(30,77,107,0.08)] dark:border-gray-700 pt-3">
                    <button
                      onClick={() => copyMessageWithSources(message)}
                      className="flex items-center space-x-1 px-3 py-1.5 text-xs text-muted hover:text-primary dark:text-gray-400 dark:hover:text-white border border-[rgba(30,77,107,0.15)] dark:border-gray-600 rounded-lg hover:bg-warm-100 dark:hover:bg-gray-700 transition-colors"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                      <span>{copiedId === `${message.content}-with-sources` ? 'Kopiert' : 'Kopieren'}</span>
                    </button>
                  </div>
                )}

                {message.structuredData && (
                  renderStructuredData(message.structuredData)
                )}

                {message.deepRISearchData && (
                  renderDeepRISearchData(message.deepRISearchData)
                )}
              </div>
            </div>
          ))}

          {effectiveIsLoading && (
            <div className="flex justify-start">
              <div className="max-w-3xl bg-white dark:bg-gray-800 border border-[rgba(30,77,107,0.1)] dark:border-gray-700 rounded-2xl p-4 shadow-soft">
                <div className="flex items-center space-x-2">
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-accent rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-accent rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                    <div className="w-2 h-2 bg-accent rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                  </div>
                  <span className="text-muted dark:text-gray-400 text-sm">Rechtsquellen werden durchsucht...</span>
                </div>
              </div>
            </div>
          )}

            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                <div className="flex items-center space-x-2">
                  <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="text-red-700 dark:text-red-400 text-sm">{error}</span>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="bg-white dark:bg-gray-800 border-t border-[rgba(30,77,107,0.1)] dark:border-gray-700 p-4">
            <form onSubmit={handleSubmit} className="flex items-end space-x-3">
              <div className="flex-1 relative">
                <textarea
                  ref={textareaRef}
                  value={inputValue}
                  onChange={(e) => { setInputValue(e.target.value); autoResizeTextarea(); }}
                  onKeyDown={handleKeyDown}
                  placeholder="Stellen Sie Ihre Rechtsfrage..."
                  rows={1}
                  className="w-full border border-[rgba(30,77,107,0.15)] dark:border-gray-600 rounded-xl px-4 py-3 focus:ring-2 focus:ring-accent focus:border-accent bg-white dark:bg-gray-700 text-primary dark:text-white placeholder-muted dark:placeholder-gray-400 transition-all resize-none overflow-y-auto"
                  style={{ maxHeight: '180px' }}
                  disabled={effectiveIsLoading}
                />
              </div>
              <div className="flex flex-col items-center space-y-1">
                {effectiveIsLoading ? (
                  <button
                    type="button"
                    onClick={() => { cancelSearch(); setIsLoading(false); }}
                    className="px-5 py-3 rounded-xl bg-red-600 hover:bg-red-700 text-white font-medium transition-colors"
                  >
                    Abbrechen
                  </button>
                ) : (
                  <button
                    type="submit"
                    disabled={!inputValue.trim()}
                    className="btn-primary px-6 py-3 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Senden
                  </button>
                )}
              </div>
            </form>
            <div className="flex items-center justify-between mt-1.5 px-1">
              <button
                type="button"
                onClick={toggleChatMode}
                className="flex items-center space-x-1.5 text-xs text-muted dark:text-gray-400 hover:text-primary dark:hover:text-gray-200 transition-colors"
                title={chatMode ? 'Chat-Modus: Enter = senden' : 'Schreib-Modus: Shift+Enter = senden'}
              >
                <span className={`inline-block w-8 h-4 rounded-full relative transition-colors ${chatMode ? 'bg-accent' : 'bg-gray-300 dark:bg-gray-600'}`}>
                  <span className={`absolute top-0.5 w-3 h-3 rounded-full bg-white shadow transition-all duration-200 ${chatMode ? 'left-[18px]' : 'left-[2px]'}`} />
                </span>
                <span>{chatMode ? 'Chat-Modus' : 'Schreib-Modus'}</span>
              </button>
              <span className="text-xs text-muted dark:text-gray-400">
                {chatMode ? 'Enter = senden, Shift+Enter = neue Zeile' : 'Shift+Enter = senden, Enter = neue Zeile'}
              </span>
            </div>
          </div>
    </div>
  );
};

export default RISAssistant;
